// VendorForm.tsx
import React, { useEffect, useState } from 'react';
import StadiumMap from './StadiumMap';
import venueSettingsService from '../../services/venueSettingsService';
import styles from './VendorForm.module.css';

interface VendorFormProps {
  onSubmit: (data: { name: string; sections: string[]; cuisine_type: string | null }) => void;
  onCancel: () => void;
  selectedSections: Set<string>;
  onSectionToggle: (sections: Set<string>) => void;
}

interface FormErrors {
  vendorName?: string;
  sections?: string;
}

const CUISINE_OPTIONS = [
  { id: 'burgers',  label: 'Burgers',  icon: '🍔' },
  { id: 'pizza',    label: 'Pizza',    icon: '🍕' },
  { id: 'chicken',  label: 'Chicken',  icon: '🍗' },
  { id: 'mexican',  label: 'Mexican',  icon: '🌮' },
  { id: 'asian',    label: 'Asian',    icon: '🍜' },
  { id: 'drinks',   label: 'Drinks',   icon: '🥤' },
  { id: 'snacks',   label: 'Snacks',   icon: '🍿' },
  { id: 'other',    label: 'Other',    icon: '🍽️' },
];

const VendorForm: React.FC<VendorFormProps> = ({ onSubmit, onCancel, selectedSections, onSectionToggle }) => {
  const [vendorName, setVendorName] = useState<string>('');
  const [cuisineType, setCuisineType] = useState<string>('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [cuisineOptions, setCuisineOptions] = useState(CUISINE_OPTIONS);
  const [venueSections, setVenueSections] = useState<string[]>([]);

  useEffect(() => {
    venueSettingsService.get().then((data) => {
      if (data.cuisineTypes?.length) {
        setCuisineOptions(
          data.cuisineTypes.map((c) => ({
            id: c.id,
            label: c.label,
            icon: c.icon || '🍽️',
          }))
        );
      }
      if (data.sections?.length) {
        setVenueSections(data.sections);
      }
    }).catch(() => {
      // keep defaults
    });
  }, []);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!vendorName.trim()) {
      newErrors.vendorName = 'Vendor name is required';
    } else if (vendorName.trim().length < 2) {
      newErrors.vendorName = 'Vendor name must be at least 2 characters';
    } else if (vendorName.trim().length > 50) {
      newErrors.vendorName = 'Vendor name must be less than 50 characters';
    }

    if (selectedSections.size === 0) {
      newErrors.sections = 'At least one section must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    
    if (validateForm()) {
      const vendorData = {
        name: vendorName.trim(),
        sections: Array.from(selectedSections),
        cuisine_type: cuisineType || null
      };
      
      onSubmit(vendorData);
      setVendorName('');
      setCuisineType('');
      setErrors({});
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setVendorName(value);
    
    if (errors.vendorName && value.trim()) {
      setErrors(prev => ({ ...prev, vendorName: undefined }));
    }
  };

  const isFormValid = vendorName.trim().length >= 2 && selectedSections.size > 0;

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      
      <div className={styles.header}>
        <p className={styles.description}>
          Create a new vendor by providing a name and selecting the stadium sections they will serve.
        </p>
      </div>

      <div className={styles.field}>
        <label className={`${styles.label} ${styles.labelRequired}`}>
          Vendor Name
        </label>
        <input
          type="text"
          value={vendorName}
          onChange={handleNameChange}
          placeholder="Enter vendor name (e.g., Stadium Grill)"
          maxLength={50}
          className={`${styles.input} ${errors.vendorName ? styles.inputError : ''}`}
        />
        {errors.vendorName && (
          <p className={styles.error}>{errors.vendorName}</p>
        )}
      </div>

      <div className={styles.field}>
        <label className={styles.label}>
          Cuisine Type
        </label>
        <select
          value={cuisineType}
          onChange={(e) => setCuisineType(e.target.value)}
          className={styles.input}
        >
          <option value="">— Select cuisine type —</option>
          {cuisineOptions.map(opt => (
            <option key={opt.id} value={opt.id}>
              {opt.icon} {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label className={`${styles.label} ${styles.labelRequired}`}>
          Stadium Sections
        </label>
        
        <StadiumMap
          selectedSections={selectedSections}
          onSectionToggle={onSectionToggle}
          sectionList={venueSections.length ? venueSections : undefined}
        />
        
        {errors.sections && (
          <p className={styles.error}>{errors.sections}</p>
        )}
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          onClick={onCancel}
          className={`${styles.button} ${styles.buttonSecondary}`}
        >
          Cancel
        </button>
        
        <button
          type="submit"
          disabled={!isFormValid}
          className={`${styles.button} ${styles.buttonPrimary}`}
        >
          Create Vendor
        </button>
      </div>

      {(vendorName.trim() || selectedSections.size > 0) && (
        <div className={styles.summary}>
          <h4 className={styles.summaryTitle}>Summary</h4>
          <div className={styles.summaryGrid}>
            <span className={styles.summaryLabel}>Name:</span>
            <span className={styles.summaryValue}>
              {vendorName.trim() || 'Not specified'}
            </span>
            
            <span className={styles.summaryLabel}>Cuisine:</span>
            <span className={styles.summaryValue}>
              {cuisineType
                ? `${CUISINE_OPTIONS.find(o => o.id === cuisineType)?.icon} ${CUISINE_OPTIONS.find(o => o.id === cuisineType)?.label}`
                : 'Not specified'}
            </span>
            
            <span className={styles.summaryLabel}>Sections:</span>
            <span className={styles.summaryValue}>
              {selectedSections.size > 0 
                ? `${selectedSections.size} selected (${Array.from(selectedSections).sort().join(', ')})`
                : 'None selected'
              }
            </span>
          </div>
        </div>
      )}
    </form>
  );
};

export default VendorForm;